import { _Product_liquid, _Variant_liquid, CartDrawerBlocksRoute_protection_product, SettingsSchema } from "_types";
import { cart } from "cart";
import { del, entries, get, set } from "idb-keyval";
import { _Cart_fetch_api, _Cart_fetch_api_Items } from "types";
import { JSONParse } from "utils";
import { create } from "zustand";
import { createJSONStorage, persist, StateStorage } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { createStore } from "zustand/vanilla";
import { CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_CHILD_KEY, CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_PARENT_KEY } from "./product-configurable-products.constants";

// State version for migration handling
const STATE_VERSION = 1;

// Memory management constants
const MAX_PRODUCTS_IN_MEMORY = 20; // Limit to 20 products in memory
const MAX_RECENTLY_VIEWED = 20; // Keep this existing limit

// State validation function
const validateState = (state: any): boolean => {
  try {
    if (!state) return false;
    if (typeof state !== 'object') return false;
    if (!Array.isArray(state.recentlyViewed)) return false;
    if (typeof state.products !== 'object') return false;
    return true;
  } catch (err) {
    console.error('State validation failed:', err);
    return false;
  }
};

// State recovery function
const recoverState = async (): Promise<any> => {
  try {
    // Clear potentially corrupted state
    await del(`${window.Shopify.shop}-accelerate-products`);
    
    // Return clean initial state
    return {
      hydrated: false,
      products: {},
      recentlyViewed: [],
      currentProductHandle: null,
      version: STATE_VERSION
    };
  } catch (err) {
    console.error('State recovery failed:', err);
    return null;
  }
};

// Memory management function - implements LRU cache
const manageCacheSize = (products: { [key: string]: _Product }): { [key: string]: _Product } => {
  const productEntries = Object.entries(products);
  
  if (productEntries.length <= MAX_PRODUCTS_IN_MEMORY) {
    return products;
  }
  
  // Sort by timestamp (most recent first) and keep only the most recent products
  const sortedProducts = productEntries
    .sort(([, a], [, b]) => (b.timestamp || 0) - (a.timestamp || 0))
    .slice(0, MAX_PRODUCTS_IN_MEMORY);
  
  const trimmedProducts = Object.fromEntries(sortedProducts);
  
  console.warn(`Product cache trimmed from ${productEntries.length} to ${MAX_PRODUCTS_IN_MEMORY} items`);
  
  return trimmedProducts;
};

// Custom storage object with enhanced error handling
const storage: StateStorage = {
  getItem: async (name: string): Promise<string | null> => {
    try {
      const data = await get(name);
      if (!data) return null;
      
      const parsed = JSONParse<{ state?: GlobalProductsState; version?: number }>(data);
      
      // Validate state and version
      if (!parsed?.state || !validateState(parsed.state) || parsed.version !== STATE_VERSION) {
        console.warn('Invalid state detected, initiating recovery');
        const recovered = await recoverState();
        return recovered ? JSON.stringify({ state: recovered, version: STATE_VERSION }) : null;
      }
      
      return data;
    } catch (err) {
      console.error('Storage getItem error:', err);
      return null;
    }
  },
  setItem: async (name: string, value: string): Promise<void> => {
    try {
      const parsed = JSONParse<{ state?: GlobalProductsState }>(value);
      
      // Don't store invalid state
      if (!parsed?.state || !validateState(parsed.state)) {
        console.warn('Attempted to store invalid state, skipping');
        return;
      }
      
      // Don't store unhydrated state if we already have data
      if (!parsed.state.hydrated && (await entries()).length) {
        return;
      }
      
      await set(name, value);
    } catch (err) {
      console.error('Storage setItem error:', err);
    }
  },
  removeItem: async (name: string): Promise<void> => {
    try {
      await del(name);
    } catch (err) {
      console.error('Storage removeItem error:', err);
    }
  },
};

export type _Product_metaobjects = { [T: string]: any[] };

export type _Product = Omit<_Product_liquid, "variants" | "selected_or_first_available_variant"> & {
  hidden?: boolean;
  timestamp?: number;
  _related?: _Product[];
  _complementary?: _Product[];
  _upsell_fetched?: number;
  variants: _Variant[];
  selected_or_first_available_variant: _Variant;
  metaobjects?: _Product_metaobjects;
};

export type _Variant = _Variant_liquid & {
  selling_plan_allocations: {
    price: number;
    compare_at_price: number;
    name: string;
    id: number;
    group_id: string;
  }[];
};

export const globalSettings = createStore<SettingsSchema>((set, get) => {
  /*Initial State*/
  return {
    ...window.theme_settings,
  };
});
export const useGlobalSettings = create(globalSettings);
window.useGlobalProducts = useGlobalSettings;

type GlobalProductsState = {
  hydrated?: boolean;
  recentlyViewed: string[];
  currentProductHandle: string | null;
  products: { [T: string]: _Product };
  getAsyncProduct: (handle: string) => Promise<_Product>;
  getAsyncProductWithUpsells: (handle: string, productId: number) => Promise<_Product>;
  getUntransformedAsyncProduct: (handle: string) => Promise<_Product>;
  getProduct: (handle: string) => _Product;
  getUntransformedProduct: (handle: string) => _Product;
  addProducts: (products: _Product[]) => void;
  version: number;
  recover: () => Promise<void>;
};

const fetching = {
  current: {},
};

export const useGlobalProducts = create(
  persist(
    immer<GlobalProductsState>((set, get) => {
      return {
        hydrated: false,
        products: {},
        recentlyViewed: [],
        currentProductHandle: null,
        version: STATE_VERSION,
        addProducts: (products) => {
          set((state) => {
            const timestamp = Date.now();
            products.forEach((product) => {
              const initialProduct = get().products[product.handle];
              state.products[product.handle] = {
                ...initialProduct,
                ...product,
                timestamp,
                _complementary: [
                  ...(product?._complementary ?? []),
                  ...(initialProduct?._complementary ?? []),
                ].filter((p, i, arr) => arr.findIndex((p2) => p2.handle === p.handle) === i),
                _related: [
                  ...(product?._related ?? []),
                  ...(initialProduct?._related ?? []),
                ].filter((p, i, arr) => arr.findIndex((p2) => p2.handle === p.handle) === i),
              };
            });
            
            // Apply memory management to prevent unbounded growth
            state.products = manageCacheSize(state.products);
          });
        },
        getAsyncProductWithUpsells: async (handle, productId) => {
          let timestamp = Date.now();
          const product = get().products[handle];
          const hasUpsells =
            product?._complementary?.length ||
            product?._related?.length ||
            timestamp - (product?._upsell_fetched ?? 0) < 1000 * 60 * 30;

          const upToDate =
            product && product?.timestamp && timestamp - product?.timestamp < 1000 * 60 * 15;

          if (hasUpsells && upToDate) {
            return window.transformProducts(product);
          }

          const localProduct: _Product = JSONParse(
            document.querySelector(`[data-product-json="${handle}"]`)?.innerHTML
          );

          if (localProduct && hasUpsells) {
            set((state) => {
              state.products = {
                ...state.products,
                [localProduct.handle]: {
                  ...product,
                  ...localProduct,
                  timestamp: timestamp,
                },
              };
              // Apply memory management
              state.products = manageCacheSize(state.products);
            });
            return {
              ...window.transformProducts({
                ...product,
                ...localProduct,
                timestamp: timestamp,
              }),
              timestamp: timestamp,
            };
          }

          if (!(handle in fetching.current)) {
            fetching.current[handle] = Promise.all([
              upToDate
                ? product
                : fetch(
                    `${window.Shopify.routes.root}products/${handle
                      .replace(/\/products\//gi, "")
                      .replace(/^\//gi, "")}?sections=product-data`
                  )
                    .then((data) => data.json())
                    .then((content) => {
                      const dataElement = document.createElement("div");
                      dataElement.innerHTML = content["product-data"];
                      return JSONParse<_Product>(dataElement.querySelector("script")?.innerHTML);
                    }),
              hasUpsells
                ? product?._related
                : fetch(
                    `${window.Shopify.routes.root}recommendations/products?product_id=${productId}&section_id=product-recommendations&limit=6&intent=related`
                  )
                    .then((content) => content.text())
                    .then((htmlString) => {
                      const dataElement = document.createElement("div");
                      dataElement.innerHTML = htmlString;
                      return (
                        JSONParse<{ type: "related" | "complementary"; products: _Product[] }>(
                          dataElement.querySelector("script")?.innerHTML
                        )?.products ?? []
                      );
                    }),
              hasUpsells
                ? product?._complementary
                : fetch(
                    `${window.Shopify.routes.root}recommendations/products?product_id=${productId}&section_id=product-recommendations&limit=6&intent=complementary`
                  )
                    .then((content) => content.text())
                    .then((htmlString) => {
                      const dataElement = document.createElement("div");
                      dataElement.innerHTML = htmlString;
                      return (
                        JSONParse<{ type: "related" | "complementary"; products: _Product[] }>(
                          dataElement.querySelector("script")?.innerHTML
                        )?.products ?? []
                      );
                    }),
            ]);
          }

          try {
            const [productData, _related, _complementary] = await fetching.current[handle];
            delete fetching.current[handle];

            timestamp = Date.now();
            const updatedProduct: _Product = {
              ...(get().products[handle] ?? {}),
              ...productData,
              _related,
              _complementary,
              _upsell_fetched: Date.now(),
              timestamp,
            };

            set((state) => {
              if (productData) {
                state.products = {
                  ...state.products,
                  [productData.handle]: updatedProduct,
                };
                // Apply memory management
                state.products = manageCacheSize(state.products);
              }
              if (!productData) {
                delete state.products[handle];
              }
            });
            return {
              ...window.transformProducts(updatedProduct),
              timestamp,
            };
          } catch (err) {
            delete fetching.current[handle];
            return null;
          }
        },
        getAsyncProduct: async (handle) => {
          const timestamp = Date.now();
          const product = get().products[handle];

          if (product && product?.timestamp && timestamp - product?.timestamp < 1000 * 60 * 15) {
            return window.transformProducts(product);
          }

          const localProduct: _Product = JSONParse(
            document.querySelector(`[data-product-json="${handle}"]`)?.innerHTML
          );

          if (localProduct) {
            set((state) => {
              state.products = {
                ...state.products,
                [localProduct.handle]: {
                  ...localProduct,
                  timestamp: timestamp,
                },
              };
              // Apply memory management
              state.products = manageCacheSize(state.products);
            });
            return {
              ...window.transformProducts(localProduct),
              timestamp: timestamp,
            };
          }

          if (!(handle in fetching.current)) {
            fetching.current[handle] = fetch(
              `${window.Shopify.routes.root}products/${handle
                .replace(/\/products\//gi, "")
                .replace(/^\//gi, "")}?sections=product-data`
            ).then((data) => data.json());
          }

          try {
            const content = { ...(await fetching.current[handle]) };
            delete fetching.current[handle];
            const dataElement = document.createElement("div");
            dataElement.innerHTML = content["product-data"];
            const productData = JSONParse<_Product>(dataElement.querySelector("script")?.innerHTML);

            set((state) => {
              if (productData) {
                state.products = {
                  ...state.products,
                  [productData.handle]: {
                    ...productData,
                    timestamp,
                  },
                };
                // Apply memory management
                state.products = manageCacheSize(state.products);
              }
              if (!productData) {
                delete state.products[handle];
              }
            });
            return {
              ...window.transformProducts(productData),
              timestamp,
            };
          } catch (err) {
            delete fetching.current[handle];
            return null;
          }
        },
        getUntransformedAsyncProduct: async (handle) => {
          const timestamp = Date.now();
          const product = get().products[handle];

          if (product && product?.timestamp && timestamp - product?.timestamp < 1000 * 60 * 15) {
            return product;
          }

          const localProduct: _Product = JSONParse(
            document.querySelector(`[data-product-json="${handle}"]`)?.innerHTML
          );

          if (localProduct) {
            set((state) => {
              state.products = {
                ...state.products,
                [localProduct.handle]: {
                  ...localProduct,
                  timestamp: timestamp,
                },
              };
              // Apply memory management
              state.products = manageCacheSize(state.products);
            });
            return {
              ...localProduct,
              timestamp: timestamp,
            };
          }

          if (!(handle in fetching.current)) {
            fetching.current[handle] = fetch(
              `${window.Shopify.routes.root}products/${handle
                .replace(/\/products\//gi, "")
                .replace(/^\//gi, "")}?sections=product-data`
            ).then((data) => data.json());
          }

          try {
            const content = { ...(await fetching.current[handle]) };
            delete fetching.current[handle];
            const dataElement = document.createElement("div");
            dataElement.innerHTML = content["product-data"];
            const productData = JSONParse<_Product>(dataElement.querySelector("script")?.innerHTML);

            set((state) => {
              if (productData) {
                state.products = {
                  ...state.products,
                  [productData.handle]: {
                    ...productData,
                    timestamp,
                  },
                };
                // Apply memory management
                state.products = manageCacheSize(state.products);
              }
              if (!productData) {
                delete state.products[handle];
              }
            });
            return {
              ...productData,
              timestamp,
            };
          } catch (err) {
            delete fetching.current[handle];
            return null;
          }
        },
        getProduct: (handle) => {
          const timestamp = Date.now();
          const localProduct: _Product = JSONParse(
            document.querySelector(`[data-product-json="${handle}"]`)?.innerHTML
          );

          if (localProduct) {
            set((state) => {
              state.products = {
                ...state.products,
                [localProduct.handle]: {
                  ...localProduct,
                  timestamp: Date.now(),
                },
              };
              // Apply memory management
              state.products = manageCacheSize(state.products);
            });
            return {
              ...window.transformProducts(localProduct),
              timestamp: timestamp,
            };
          }

          const product = get().products[handle];

          if (product) {
            if (product && product?.timestamp && timestamp - product?.timestamp > 1000 * 60 * 15) {
              /* State While Revalidate */
              get().getAsyncProduct(handle);
            }
            return window.transformProducts(product);
          }

          return null;
        },
        getUntransformedProduct: (handle) => {
          const localProduct: _Product = JSONParse(
            document.querySelector(`[data-product-json="${handle}"]`)?.innerHTML
          );

          if (localProduct) {
            set((state) => {
              state.products = {
                ...state.products,
                [localProduct.handle]: {
                  ...localProduct,
                  timestamp: Date.now(),
                },
              };
              // Apply memory management
              state.products = manageCacheSize(state.products);
            });
            return {
              ...localProduct,
              timestamp: Date.now(),
            };
          }

          const product = get().products[handle];

          if (product) {
            return product;
          }

          return null;
        },
        recover: async () => {
          const recovered = await recoverState();
          if (recovered) {
            set((state) => ({
              ...recovered,
              version: STATE_VERSION
            }));
          }
        },
      };
    }),
    {
      name: `${window.Shopify.shop}-accelerate-products`,
      version: STATE_VERSION,
      storage: createJSONStorage(() => storage),
      partialize: (state) => ({
        recentlyViewed: state.recentlyViewed,
        hydrated: state.hydrated,
        currentProductHandle: state.currentProductHandle,
        version: state.version,
        // Explicitly excluding 'products' to prevent memory accumulation
        // Products will be fetched fresh on each session
      }),
      onRehydrateStorage: (state) => {
        return (state2, error) => {
          console.log("Rehydrate Storage");
          
          if (error) {
            console.error('Hydration error:', error);
            // Attempt recovery on hydration error
            useGlobalProducts.getState().recover();
            return;
          }
          
          if (!state2 || !validateState(state2)) {
            console.warn('Invalid state after hydration, initiating recovery');
            useGlobalProducts.getState().recover();
            return;
          }
          
          try {
            const handle = window.location.pathname
              .split("?")[0]
              .replace(/.*\/products\/([^?]*)/gi, "$1")
              .toLowerCase();
              
            if (handle) {
              const product = Object.values(state2.products).find((p) => p.handle === handle);
              if (product) {
                const recentlyViewed = [
                  ...new Set([product.handle, ...state2.recentlyViewed]),
                ].slice(0, MAX_RECENTLY_VIEWED);

                useGlobalProducts.setState({
                  recentlyViewed: recentlyViewed,
                });
              }
            }
            
            useGlobalProducts.setState({
              hydrated: true,
              currentProductHandle:
                document.querySelector<HTMLElement>("[data-product-handle]")?.dataset
                  ?.productHandle ?? null,
            });
          } catch (err) {
            console.error('Error during hydration:', err);
            useGlobalProducts.getState().recover();
          }
        };
      },
    }
  )
);

export type GiftItemType = {
  content: string;
  type: "quantity" | "price";
  target: number;
  target_collection: string;
  target_products: string[];
  receives_quantity: number;
  receives_discount_percentage: number;
  receives_products: string[];
};
window.useGlobalProducts = useGlobalProducts;

export type CartDrawerState = {
  loading: boolean;
  cartData: _Cart_fetch_api | null;
  filteredCartData: () => _Cart_fetch_api | null;
  initCart: () => Promise<void>;
  addItem: (variant: Pick<_Variant, "id" | "image">, quantity: number) => Promise<void>;
  adjustItem: (lineItem: _Cart_fetch_api_Items, quantity: number, index: number) => void;
  routeProtectionProducts: CartDrawerBlocksRoute_protection_product["settings"][];
  updates: { [T: string | number]: number } | number[] | null;
  fetchUpdates: () => Promise<void>;
};

const getFilteredCartData = (cartData: _Cart_fetch_api) => {
  const filteredItems = cartData.items?.filter(
    (item) => !item.properties[CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_CHILD_KEY]
  ) ?? [];

  if (!filteredItems.length) return { ...cartData, items: [], item_count: 0, total_price: 0 };

  // Recalculate totals for the filtered cart data
  const filteredItemCount = filteredItems.reduce((sum, item) => sum + item.quantity, 0);
  const filteredTotalPrice = filteredItems.reduce((sum, item) => sum + item.final_line_price, 0);

  return {
    ...cartData,
    items: filteredItems,
    item_count: filteredItemCount,
    total_price: filteredTotalPrice,
  };
};

export const cartDrawer = createStore<CartDrawerState>((set, get) => {
  /*Initial State*/
  const cartData = window.cartData;
  const routeProtectionProducts = [];
  document.querySelectorAll<HTMLElement>("[data-route-protection]").forEach((element) => {
    const settings = JSONParse<CartDrawerBlocksRoute_protection_product["settings"]>(
      element.dataset.routeProtectionSettings
    );
    routeProtectionProducts.push(settings);
  });
  

  return {
    loading: true,
    cartData: cartData,
    // Get filtered cart data for UI
    filteredCartData: () => {
      const cartData = get().cartData;
      if (!cartData) return null;
      return getFilteredCartData(cartData);
    },
    routeProtectionProducts: routeProtectionProducts,
    updates: null,
    initCart: async () => {
      set({ loading: true });
      set({ cartData: await cart.get(), loading: false });
    },
    adjustItem: (lineItem, quantity, index) => {
      const isParentChanging = !!lineItem.properties["_configurable_products_uuid"];

      const newQuantity = Math.max(0, lineItem.quantity + quantity);
      
      set(({ cartData }) => {
        const parentUuid = lineItem.properties[CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_PARENT_KEY];
    
        const updatedItems = cartData.items.map((item, i) => {
          const isChild = item.properties[CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_CHILD_KEY] === parentUuid;
          
          // Handle parent item quantity adjustment
          if (lineItem.key === item.key || (isParentChanging && isChild)) {
            return {
              ...item,
              quantity: newQuantity,
              final_line_price: item.final_price * newQuantity,
              original_line_price: item.original_price * newQuantity,
            };
          }
          
          return item;
        });

        // Recalculate `updates` based on the new quantities
        const newUpdates = cartData.items.map((item, i) => {
          const isChild = item.properties[CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_CHILD_KEY] === parentUuid;
          // If the parent or its child is being adjusted
          if (
            lineItem.key === item.key || // Parent itself
            (isParentChanging && isChild)
          ) {
            return newQuantity; // Use the new quantity for both parent and children
          }
          return item.quantity; // Keep other items unchanged
        });
    
        // Recalculate totals
        const updatedItemCount = updatedItems.reduce((sum, item) => sum + item.quantity, 0);
        const updatedTotalPrice = updatedItems.reduce((sum, item) => sum + item.final_line_price, 0);
    
        return {
          loading: true,
          cartData: {
            ...cartData,
            items: updatedItems,
            item_count: updatedItemCount,
            total_price: updatedTotalPrice,
          },
          updates: newUpdates, // Updates array follows the provided structure
        };
      });
    },
    fetchUpdates: async () => {
      const data = await cart.update({
        updates: get().updates,
      });
      set({ cartData: data, loading: false, updates: null });
    },
    addItem: async (variant, quantity) => {},
  };
});
export const useCartDrawer = create(cartDrawer);
window.useCartDrawer = useCartDrawer;
